import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getObjectId, Order, OrderCancellationReason } from '@hero/schema';
import {
  getOrderNextAction,
  isGovernmentWaybillStateInert,
  resolveGovernmentWaybillState,
} from '@hero/core';
import {
  useAppWaybillBayanStatusApi,
  useAppsInstalledApi,
  useOrderGenerateWaybillApi,
  useOrderUpdateApi,
} from '@hero/api-hooks';
import { useLogistiFiling } from '@hero/hooks-shared';
import { ActionButton, ActionButtonGroup, AppLogo } from '@hero/web-components';
import { LOGISTI_APP, WHATSAPP_APP } from '@hero/apps';
import { useAppConnectorLive, useGovernmentWaybillPdf, useViewerLeg } from '@hero/web-hooks';
import { useFeature } from '@hero/web-context';
import { useCurrentUser, useOrderActions } from '@hero/hooks-shared';
import {
  CloseIcon,
  LoadMainIcon,
  OrderWaybillIcon,
  PencilIcon,
  RefreshIcon,
  UserIcon,
  ZapIcon,
} from '@hero/web-icons';
import { OrderUpdateStatusModal } from './OrderUpdateStatusModal';
import { OrderCancelModal } from './OrderCancelModal';
import { OrderSendWaybillModal } from './OrderSendWaybillModal';
import { OrderChangeOwnerModal } from './OrderChangeOwnerModal';
import { OrderChangeClientModal } from './OrderChangeClientModal';
import { LogistiConnectModal } from '../../Apps/LogistiConnectModal';
import { GovernmentWaybillDrawer } from '../../Apps/GovernmentWaybillDrawer';
import { OrderDriverPickerDrawer } from '../Drawer/OrderDriverPickerDrawer';
import { OrderPriceEditDrawer } from '../Drawer/OrderPriceEditDrawer';
import { OrderLoadEditDrawer } from '../Drawer/OrderLoadEditDrawer';

export interface OrderActionsProps {
  order: Order;
  /**
   * Optional override hook for the cancel flow. When omitted (default
   * for both customer and carrier in the unified dashboard) the
   * shared `OrderCancelModal` is mounted in-place and the cancel
   * mutation goes through `useOrderUpdateApi` (the ambient SDK
   * provided by `<ApiProvider>`). Pass a callback only if you need
   * to spawn a custom modal — e.g. an admin tool that wraps cancel
   * with extra confirmation steps.
   */
  onCancelOrder?: () => void;
  /** Pair with `onCancelOrder`; ignored when the shared modal is used. */
  isCanceling?: boolean;
  displayForUser?: 'customer' | 'carrier';
  className?: string;
  buttonLabel?: string;
  /**
   * Controlled open state for the change-client modal so the drawer
   * summary chip can open the same modal as the actions menu.
   */
  changeClientOpen?: boolean;
  onChangeClientOpenChange?: (open: boolean) => void;
}

export const OrderActions: React.FC<OrderActionsProps> = ({
  order,
  onCancelOrder,
  isCanceling: isCancelingProp = false,
  displayForUser = 'customer',
  className,
  buttonLabel,
  changeClientOpen,
  onChangeClientOpenChange,
}) => {
  const { t } = useTranslation(['m-order', 'm-apps']);
  const { isEnabled: canEditOrder } = useFeature('order_module', 'sub_edit_order');
  const isLogistiLive = useAppConnectorLive(LOGISTI_APP.id);
  const viewerLeg = useViewerLeg(order);
  const { orgId, user } = useCurrentUser();
  const isViewerShipper = useMemo(() => {
    const shipperId = order.shipper?._id?.toString();
    if (!shipperId) return false;
    if (orgId && orgId.toString() === shipperId) return true;
    const userId = (user as { _id?: { toString(): string } } | undefined)?._id?.toString?.();
    return Boolean(userId && userId === shipperId);
  }, [order.shipper?._id, orgId, user]);
  const {
    isCancelable,
    canDownloadWaybill,
    isWaybillGenerating,
    canRegenerateWaybill,
    canAssignDriver,
    canEditPrice,
    cancelOptions,
  } = useOrderActions(order, { displayForUser, viewerLeg, isViewerShipper });
  const [isUpdateStatusOpen, setIsUpdateStatusOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isPriceEditOpen, setIsPriceEditOpen] = useState(false);
  const [isLoadEditOpen, setIsLoadEditOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isSendWaybillOpen, setIsSendWaybillOpen] = useState(false);
  const [isChangeOwnerOpen, setIsChangeOwnerOpen] = useState(false);
  const [internalChangeClientOpen, setInternalChangeClientOpen] = useState(false);
  const isChangeClientOpen = changeClientOpen ?? internalChangeClientOpen;
  const setIsChangeClientOpen = onChangeClientOpenChange ?? setInternalChangeClientOpen;
  /** When true, assigning a client should then open the price drawer. */
  const [openPriceAfterClient, setOpenPriceAfterClient] = useState(false);
  const [isConnectLogistiOpen, setIsConnectLogistiOpen] = useState(false);
  const [isGovernmentWaybillOpen, setIsGovernmentWaybillOpen] = useState(false);

  // One cached query for the whole session — a tenant's ability to issue changes
  // only when it connects, disconnects, or saves a CR, and all three mutations
  // invalidate the `app` entity, so this stays correct without any per-order
  // refetch. It used to be two: `canIssue` had its own endpoint back when
  // filing on Hero's licence wrote no install row for this list to read.
  const isCarrierView = displayForUser === 'carrier';
  const { canIssue: canIssueWaybill } = useLogistiFiling({
    enabled: isLogistiLive && isCarrierView,
  });
  const { data: installedApps } = useAppsInstalledApi({
    enabled: isLogistiLive && isCarrierView,
  });
  const logistiInstall = installedApps?.find(app => app.appId === 'logisti');

  const { data: governmentWaybillStatus, isPending: isGovernmentWaybillStatusPending } =
    useAppWaybillBayanStatusApi(String(order._id), {
      enabled: isLogistiLive && isCarrierView,
    });

  const generateWaybill = useOrderGenerateWaybillApi({
    onSuccess: () => {
      toast.success(
        t('m-order:actions.regenerate_waybill_started', {
          defaultValue: 'Waybill generation started',
        }),
      );
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const governmentWaybillBlockers = governmentWaybillStatus?.blockers ?? [];
  const governmentWaybillPdf = useGovernmentWaybillPdf(order, {
    pdfUrl: governmentWaybillStatus?.pdfUrl,
    onError: () => toast.error(t('m-apps:toast.error.download_failed')),
  });

  // Shared with carrier-app's actions sheet so the two surfaces can't drift on
  // which state wins — see `resolveGovernmentWaybillState` for the ordering.
  const governmentWaybillState = resolveGovernmentWaybillState({
    order,
    status: governmentWaybillStatus,
    statusFetched: !isGovernmentWaybillStatusPending,
    canIssue: canIssueWaybill,
  });

  const handleGovernmentWaybillClick = () => {
    switch (governmentWaybillState) {
      case 'connect':
        setIsConnectLogistiOpen(true);
        return;
      case 'download':
        governmentWaybillPdf.open();
        return;
      // Issuing always goes through the drawer, even when nothing is missing.
      // It is where the fields, the progress indicator, and the result live, so
      // a one-click issue from the menu would be the only path that reported
      // its outcome in a toast instead of on the surface that asked for it.
      case 'blocked':
      case 'failed':
      case 'ready':
        setIsGovernmentWaybillOpen(true);
        return;
      default:
        return;
    }
  };

  const governmentWaybillLabel = t(
    `m-apps:waybill.action.${governmentWaybillState}.label`,
    { defaultValue: t('m-apps:waybill.action_label') },
  );

  const governmentWaybillDescription =
    governmentWaybillState === 'blocked'
      ? t('m-apps:waybill.action.blocked.description', {
          count: governmentWaybillBlockers.length,
        })
      : governmentWaybillState === 'failed'
        ? (governmentWaybillStatus?.lastError ??
          order.waybillBayan?.error ??
          t('m-apps:waybill.action.failed.description'))
        : t(`m-apps:waybill.action.${governmentWaybillState}.description`, {
            defaultValue: '',
          }) || undefined;

  const isGovernmentWaybillDisabled =
    isGovernmentWaybillStatusPending || isGovernmentWaybillStateInert(governmentWaybillState);

  // Built-in cancel flow — used when no `onCancelOrder` override is
  // provided. Reuses the same `useOrderUpdateApi` plumbing that all
  // other order mutations go through, so cache invalidation and the
  // ambient SDK selection (carrier vs customer) come for free from
  // `<ApiProvider>`. The role-aware reasons list is selected via
  // `cancelOptions` (see `useOrderCancelOptions`).
  const updateOrder = useOrderUpdateApi({
    onSuccess: () => {
      toast.success(t('m-order:toast.success.order_canceled'));
      setIsCancelOpen(false);
    },
    onError: (error: Error) => {
      toast.error(t('m-order:toast.error.order_cancel', { message: error.message }));
    },
  });

  const handleSharedCancel = (reason: OrderCancellationReason, internalNote?: string) => {
    updateOrder.mutate({
      _id: getObjectId(order._id),
      action: 'cancel_order' as const,
      cancellation: {
        reason,
        ...(internalNote?.trim() && { internalNote: internalNote.trim() }),
      },
    });
  };

  const isUsingSharedCancel = !onCancelOrder;
  const isCanceling = isUsingSharedCancel ? updateOrder.isPending : isCancelingProp;
  const cancelOptionsForRole =
    displayForUser === 'carrier' ? cancelOptions.carrierOptions : cancelOptions.customerOptions;

  // Carrier-side post-create surface for the order originator. We
  // gate behind `canEditOrder` (the same flag that controls the
  // "update status" path) so internal drivers/customers don't see
  // these. `isCancelable` is reused as a rough proxy for "still
  // mutable" — once the order ships these inputs become noise.
  const canManagePost = displayForUser === 'carrier' && canEditOrder && isCancelable;

  // Assign / change driver is locked only for terminal states
  // (`delivered` / `canceled` via `canAssignDriver`) — not the
  // narrower cancelability window. A load in transit must still
  // allow swapping the driver.
  const hasDriver = !!order.driver?._id;
  const canAssignOrChangeDriver =
    displayForUser === 'carrier' && canEditOrder && canAssignDriver;

  // Walk-in / no-client: shipper can still start the price flow, but
  // must assign a CRM client first (seeds contracts[0]).
  const needsClientBeforePrice = canManagePost && !order.client;
  const showEditPrice = canManagePost && (canEditPrice || needsClientBeforePrice);

  const openEditPrice = () => {
    if (needsClientBeforePrice) {
      setOpenPriceAfterClient(true);
      setIsChangeClientOpen(true);
      return;
    }
    setIsPriceEditOpen(true);
  };

  const handleClientModalClose = () => {
    setIsChangeClientOpen(false);
    setOpenPriceAfterClient(false);
  };

  const handleClientAssigned = () => {
    if (openPriceAfterClient) {
      setOpenPriceAfterClient(false);
      setIsPriceEditOpen(true);
    }
  };

  const nextStep = getOrderNextAction(order.status.order);
  const nextStatusLabel = t(`actions.order_action_labels.${order.status.order}`, {
    defaultValue: '',
  });
  const showUpdateStatus = displayForUser === 'carrier' && !!nextStep && canEditOrder;

  const handleDownloadWaybill = () => {
    const waybillUrl = order.waybill?.image?.url;

    if (waybillUrl) {
      window.open(waybillUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleRegenerateWaybill = () => {
    if (!canRegenerateWaybill || generateWaybill.isPending) return;
    generateWaybill.mutate({ id: getObjectId(order._id) });
  };

  const handleCancelOrder = () => {
    if (!isCancelable || !canEditOrder) return;
    if (onCancelOrder) {
      onCancelOrder();
      return;
    }
    setIsCancelOpen(true);
  };

  const cancelDisabled = !isCancelable || !canEditOrder;
  const cancelDisabledDescription = !canEditOrder
    ? t('actions.edit_order_disabled')
    : !isCancelable
    ? t('actions.cancel_order_disabled')
    : undefined;

  const cancelDescription = isCancelable ? t('actions.cancel_order_description') : undefined;

  const actions: ActionButtonGroup[] = [
    {
      id: 'update-status',
      label: nextStatusLabel,
      description: t('actions.update_status_description', { status: nextStatusLabel }),
      icon: <ZapIcon className="w-4 h-4" />,
      highlight: true,
      onClick: () => setIsUpdateStatusOpen(true),
      hide: !showUpdateStatus,
    },
    {
      id: 'assign-driver',
      label: hasDriver ? t('actions.change_driver') : t('actions.assign_driver'),
      description: hasDriver
        ? t('actions.change_driver_description')
        : t('actions.assign_driver_description'),
      icon: <UserIcon className="w-4 h-4" />,
      onClick: () => setIsAssignOpen(true),
      hide: !canAssignOrChangeDriver,
    },
    {
      id: 'edit-price',
      label: needsClientBeforePrice
        ? t('actions.add_price')
        : t('actions.edit_price'),
      description: needsClientBeforePrice
        ? t('actions.add_price_needs_client_description')
        : t('actions.edit_price_description'),
      icon: <PencilIcon className="w-4 h-4" />,
      onClick: openEditPrice,
      hide: !showEditPrice,
    },
    {
      id: 'edit-load',
      label: t('actions.edit_load'),
      description: t('actions.edit_load_description'),
      icon: <LoadMainIcon className="w-4 h-4" />,
      onClick: () => setIsLoadEditOpen(true),
      hide: !canManagePost,
    },
    {
      id: 'download-waybill',
      label: t('actions.download_waybill'),
      description:
        canDownloadWaybill && !isWaybillGenerating
          ? t('actions.download_waybill_description')
          : undefined,
      disabledDescription: isWaybillGenerating
        ? t('actions.waybill_generating_description', {
            defaultValue: 'Generating waybill… Usually ready in a few seconds.',
          })
        : !canDownloadWaybill
          ? t('actions.download_waybill_disabled')
          : undefined,
      icon: <OrderWaybillIcon className="w-4 h-4" />,
      disabled: isWaybillGenerating || !canDownloadWaybill,
      isLoading: isWaybillGenerating,
      onClick: handleDownloadWaybill,
      trailingAction:
        order.driver?._id && order.vehicle?._id
          ? {
              icon: <RefreshIcon className="w-4 h-4" />,
              ariaLabel: t('actions.regenerate_waybill', {
                defaultValue: 'Regenerate waybill',
              }),
              title: t('actions.regenerate_waybill', {
                defaultValue: 'Regenerate waybill',
              }),
              disabled: !canRegenerateWaybill || generateWaybill.isPending,
              isLoading: generateWaybill.isPending,
              onClick: handleRegenerateWaybill,
            }
          : undefined,
    },
    {
      // One entry for the whole connector. There is deliberately no
      // regenerate affordance: Bayan exposes issue and cancel only, so
      // "regenerate" means voiding a legal document and issuing a
      // replacement. That happens as a consequence of a real change — moving
      // a stop to another city — not as a button someone can press twice.
      id: 'government-waybill',
      label: governmentWaybillLabel,
      description: isGovernmentWaybillDisabled ? undefined : governmentWaybillDescription,
      disabledDescription: isGovernmentWaybillDisabled
        ? governmentWaybillDescription
        : undefined,
      icon: (
        <AppLogo
          name={t('m-apps:logisti.name')}
          logo={LOGISTI_APP.logo}
          compact
          className="size-4"
        />
      ),
      disabled: isGovernmentWaybillDisabled,
      isLoading:
        governmentWaybillState === 'issuing' || governmentWaybillPdf.isPending,
      onClick: handleGovernmentWaybillClick,
      hide: !isCarrierView || !isLogistiLive,
    },
    {
      id: 'send-waybill',
      label: t('actions.send_waybill'),
      description: canDownloadWaybill
        ? t('actions.send_waybill_description')
        : undefined,
      disabledDescription: !canDownloadWaybill
        ? t('actions.send_waybill_disabled')
        : undefined,
      icon: (
        <AppLogo name="WhatsApp" logo={WHATSAPP_APP.logo} compact className="size-4" />
      ),
      disabled: !canDownloadWaybill,
      onClick: () => setIsSendWaybillOpen(true),
      hide: !canManagePost,
    },
    {
      id: 'change-order-owner',
      label: t('actions.change_order_owner'),
      description: t('actions.change_order_owner_description'),
      icon: <UserIcon className="w-4 h-4" />,
      onClick: () => setIsChangeOwnerOpen(true),
      hide: !canManagePost,
    },
    {
      id: 'change-client',
      label: order.client?.name?.trim()
        ? t('actions.change_client')
        : t('actions.assign_client'),
      description: order.client?.name?.trim()
        ? t('actions.change_client_description')
        : t('actions.assign_client_description'),
      icon: <UserIcon className="w-4 h-4" />,
      onClick: () => setIsChangeClientOpen(true),
      hide: !canManagePost,
    },
    {
      id: 'cancel-order',
      label: t('actions.cancel_order'),
      description: cancelDescription,
      disabledDescription: cancelDisabledDescription,
      icon: <CloseIcon className="w-4 h-4" />,
      disabled: cancelDisabled,
      isLoading: isCanceling,
      onClick: handleCancelOrder,
    },
  ];

  const visibleActions = actions.filter(action => !action.hide);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      <ActionButton
        actions={visibleActions}
        buttonLabel={buttonLabel || t('actions.button_label')}
        buttonClassName=""
      />
      {showUpdateStatus && (
        <OrderUpdateStatusModal
          order={order}
          isOpen={isUpdateStatusOpen}
          onClose={() => setIsUpdateStatusOpen(false)}
        />
      )}
      {canAssignOrChangeDriver && (
        <OrderDriverPickerDrawer
          order={order}
          isOpen={isAssignOpen}
          onClose={() => setIsAssignOpen(false)}
        />
      )}
      {canManagePost && (
        <>
          <OrderPriceEditDrawer
            order={order}
            canEditPrice={canEditPrice || needsClientBeforePrice}
            isOpen={isPriceEditOpen}
            onClose={() => setIsPriceEditOpen(false)}
          />
          <OrderLoadEditDrawer
            order={order}
            isOpen={isLoadEditOpen}
            onClose={() => setIsLoadEditOpen(false)}
          />
          <OrderSendWaybillModal
            order={order}
            isOpen={isSendWaybillOpen}
            onClose={() => setIsSendWaybillOpen(false)}
          />
          <OrderChangeOwnerModal
            order={order}
            isOpen={isChangeOwnerOpen}
            onClose={() => setIsChangeOwnerOpen(false)}
          />
          <OrderChangeClientModal
            order={order}
            isOpen={isChangeClientOpen}
            purpose={openPriceAfterClient ? 'before_price' : 'default'}
            onClose={handleClientModalClose}
            onAssigned={handleClientAssigned}
          />
        </>
      )}
      {isLogistiLive && isCarrierView && (
        <>
          <LogistiConnectModal
            isOpen={isConnectLogistiOpen}
            onClose={() => setIsConnectLogistiOpen(false)}
            install={logistiInstall}
          />
          <GovernmentWaybillDrawer
            isOpen={isGovernmentWaybillOpen}
            onClose={() => setIsGovernmentWaybillOpen(false)}
            order={order}
          />
        </>
      )}
      {isUsingSharedCancel && (
        <OrderCancelModal
          isOpen={isCancelOpen}
          onClose={() => setIsCancelOpen(false)}
          onConfirm={handleSharedCancel}
          isCanceling={isCanceling}
          cancelOptions={cancelOptionsForRole}
        />
      )}
    </div>
  );
};